import { Button } from "@/components/Base/Button";
import { Textarea } from "@/components/Base/Textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Card, Note, TerminologyOptions, IconNames } from "@/types/v2";
import { SendIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { addNote, deleteNote, updateNote } from "@/lib/cards";
import { firestoreToDateTime } from "@/lib/utils";
import { useUserQuery } from "@/hooks/queries/useUserQuery";
import { useInvalidateCardDetail } from "@/hooks/queries/useCardDetailQuery";
import { useState } from "react";
import { Tooltip } from "@/components/Common/Tooltip";
import { Spinner } from "@/components/Common/Spinner";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { differenceInSeconds, format } from "date-fns";
import { Icon } from "@/components/Common/Icon";
import { UserAvatar } from "@/components/Users/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { useEffect } from "react";

const noteFormSchema = z.object({
  notes: z.string().min(1, "Note cannot be empty"),
});

/**
 * Format date for display in timeline
 */
function formatDate(dt?: Date): string {
  if (!dt) return "";
  try {
    return format(dt, "MMM d, yyyy h:mm a");
  } catch {
    return dt.toString();
  }
}

interface NoteTimelineEntryProps {
  note: Note;
  shopId: string;
  cardData: Card;
  disabled?: boolean;
}

const NoteTimelineEntry = ({
  note,
  shopId,
  cardData,
  disabled,
}: NoteTimelineEntryProps) => {
  const { data: author } = useUserQuery(note.createdBy);
  const { data: editor } = useUserQuery(note.updatedBy ?? "");
  const invalidateCardDetail = useInvalidateCardDetail();

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noteText, setNoteText] = useState(note.text);

  // Synchronize local noteText state with prop when not editing
  // This prevents race conditions with real-time updates
  useEffect(() => {
    if (!isEditing) {
      setNoteText(note.text);
    }
  }, [note.text, isEditing]);

  const createdAt = firestoreToDateTime(note.createdAt);
  const updatedAt = firestoreToDateTime(note.updatedAt);
  const createdAtText = formatDate(createdAt);
  const isEdited =
    updatedAt && createdAt && differenceInSeconds(updatedAt, createdAt) > 1;

  const displayAuthor =
    updatedAt && updatedAt !== createdAt && editor ? editor : author;

  const handleDelete = () => {
    setIsSubmitting(true);
    return deleteNote(shopId, note.id, cardData.id, cardData)
      .then(() => {
        invalidateCardDetail(shopId, cardData.id);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleUpdate = () => {
    setIsSubmitting(true);
    return updateNote(shopId, note.id, noteText, cardData.id, cardData)
      .then(() => {
        setIsEditing(false);
        invalidateCardDetail(shopId, cardData.id);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsDeleting(false);
    setNoteText(note.text);
  };

  const handleMenuSelect = (action: "edit" | "delete") => {
    if (action === "edit") {
      setIsEditing(true);
    } else if (action === "delete") {
      setIsDeleting(true);
    }
    setMenuOpen(false);
  };

  return (
    <div className="rounded-lg border bg-card text-primary transition-all duration-200 hover:shadow-md">
      <div className="p-4">
        {/* Header with author and timestamp */}
        <div className="mb-3 flex items-center gap-3">
          {/* Avatar */}
          <UserAvatar
            user={displayAuthor}
            bgColor={displayAuthor?.profileColor}
            className={"h-8 w-8 text-xs"}
          />

          {/* Author info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">
                {displayAuthor?.displayName || "Unknown User"}
              </p>
              {isEdited && (
                <Tooltip
                  asChild
                  message={
                    editor ? (
                      <div className="flex items-center gap-2">
                        <UserAvatar
                          user={editor}
                          bgColor={editor?.profileColor}
                          className={"h-6 w-6 text-2xs"}
                          fallbackSize={14}
                        />
                        <span>Edited by {editor.displayName}</span>
                        <span>•</span>
                        <span>{formatDate(updatedAt)}</span>
                      </div>
                    ) : (
                      <span>Edited at {formatDate(updatedAt)}</span>
                    )
                  }
                >
                  <span className="text-xs text-muted-foreground">
                    (edited)
                  </span>
                </Tooltip>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              <Tooltip
                message={
                  displayAuthor ? (
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        user={displayAuthor}
                        bgColor={displayAuthor?.profileColor}
                        className={"h-6 w-6 text-2xs"}
                        fallbackSize={14}
                      />
                      <span>Created by {displayAuthor.displayName}</span>
                      <span>•</span>
                      <span>{formatDate(createdAt)}</span>
                    </div>
                  ) : (
                    <span>Created at {formatDate(createdAt)}</span>
                  )
                }
              >
                {createdAtText}
              </Tooltip>
            </p>
          </div>

          {/* Actions */}
          {!disabled && !isEditing && !isDeleting && (
            <div className="flex items-center gap-1 print:hidden">
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger
                  disabled={disabled}
                  className="z-5 cursor-pointer rounded-sm p-1 text-accent hover:bg-white/50 disabled:cursor-default disabled:hover:bg-transparent"
                  onMouseDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Icon name={IconNames.moreVertical} size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  onMouseDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  className="flex flex-col gap-2 p-2"
                >
                  <DropdownMenuItem onClick={() => handleMenuSelect("delete")}>
                    <Icon name={IconNames.delete} size={14} />
                    <span>Delete Note</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleMenuSelect("edit")}>
                    <Icon name={IconNames.edit} size={14} />
                    <span>Edit Note</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Note content */}
        <div className="mb-4">
          {isEditing ? (
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="min-h-[100px] resize-none"
              placeholder="Edit your note..."
              autoFocus
            />
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {note.text}
            </p>
          )}
        </div>

        {/* Action buttons when editing/deleting */}
        {(isEditing || isDeleting) && (
          <div className="flex items-center justify-end gap-2 border-t pt-3">
            {isDeleting ? (
              <>
                <span className="mr-auto text-sm text-destructive">
                  Are you sure you want to delete this note?
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Spinner /> : "Delete"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleUpdate}
                  disabled={isSubmitting || noteText.trim() === ""}
                >
                  {isSubmitting ? <Spinner /> : "Save Changes"}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NotesTimeline = ({ cardData, direction, ...props }: NotesMainProps) => {
  const { terminolize } = useTerminologyReplacer();
  const sortedNotes =
    cardData.notes?.slice().sort((a, b) => {
      const timeA = firestoreToDateTime(a.createdAt)?.getTime() ?? 0;
      const timeB = firestoreToDateTime(b.createdAt)?.getTime() ?? 0;

      if (direction === "btt") {
        return timeA - timeB; // oldest first
      }
      return timeB - timeA; // newest first
    }) ?? [];

  if (sortedNotes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 flex items-center justify-center">
          <Icon
            name={IconNames.message}
            size={24}
            className="text-muted-foreground"
          />
        </div>
        <h3 className="mb-2 text-lg font-medium text-muted-foreground">
          No Notes Yet
        </h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          Notes you add to this{" "}
          {terminolize(
            "repair order",
            TerminologyOptions.repairOrder,
            "singular"
          )}{" "}
          will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedNotes.map((note) => (
        <NoteTimelineEntry
          key={note.id}
          note={note}
          cardData={cardData}
          {...props}
        />
      ))}
    </div>
  );
};

export interface NotesMainProps {
  shopId: string;
  cardData: Card;
  disabled?: boolean;
  direction?: "ttb" | "btt"; // top-to-bottom or bottom-to-top
  autoFocusNewNote?: boolean; // Whether to auto-focus the new note input
}

export const NotesMain = ({
  shopId,
  cardData,
  disabled,
  direction = "btt",
  autoFocusNewNote = true,
}: NotesMainProps) => {
  const { terminolize } = useTerminologyReplacer();
  const invalidateCardDetail = useInvalidateCardDetail();

  const form = useForm<z.infer<typeof noteFormSchema>>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      notes: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof noteFormSchema>) => {
    await addNote(shopId, values.notes, cardData.id, cardData);
    invalidateCardDetail(shopId, cardData.id);
    form.reset();
  };

  return (
    <div className="w-full space-y-6">
      {/* Notes timeline */}
      {direction === "btt" && (
        <NotesTimeline
          cardData={cardData}
          shopId={shopId}
          disabled={disabled}
          direction={direction}
        />
      )}
      {/* Add new note form */}
      {!disabled && (
        <div className="rounded-lg border bg-card p-4 print:hidden">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-muted-foreground">
                      Add a note to this{" "}
                      {terminolize(
                        "RO",
                        TerminologyOptions.repairOrder,
                        "acronym"
                      )}
                    </FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Textarea
                          placeholder="Write a note..."
                          className="min-h-[100px] resize-none pr-12 text-primary"
                          disabled={form.formState.isSubmitting}
                          autoFocus={autoFocusNewNote}
                          {...field}
                        />
                      </FormControl>
                      <div className="absolute bottom-3 right-3">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            form.formState.isSubmitting || !field.value.trim()
                          }
                          className="h-8 w-8 p-0"
                        >
                          {form.formState.isSubmitting ? (
                            <Spinner />
                          ) : (
                            <SendIcon className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
      )}

      {/* Notes timeline */}
      {direction === "ttb" && (
        <NotesTimeline
          cardData={cardData}
          shopId={shopId}
          disabled={disabled}
          direction={direction}
        />
      )}
    </div>
  );
};
